import React, { useState } from "react";
import { API_URL, baseUrl } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";
import axios from "axios";
import { toast } from "react-toastify";

const UserCreation = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    user_type: "",
    business_name: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async () => {
    if (
      !formData.email ||
      !formData.password ||
      !formData.user_type ||
      !formData.business_name
    ) {
      toast.error("Please provide all required fields");
      return;
    }

    // Password validation: One capital letter, 1 special character, digits
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).+$/;
    if (!passwordRegex.test(formData.password)) {
      toast.error(
        "Password must be at least 8 characters long and include at least one uppercase letter, one digit, and one special character. Example: Chris@289"
      );
      return;
    }

    setLoading(true);
    try {
      // Step 1: Register User (No token used as per requirement)
      const registerPayload = {
        password: formData.password,
        user_type: formData.user_type,
        email: formData.email,
        firstname: formData.business_name,
        lastname: formData.business_name,
        last_level: 1,
      };

      const registerRes = await axios.post(
        `${baseUrl}${API_URL.REGISTER_USER}`,
        registerPayload,
        {
          headers: {
            version: "v1",
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Registration Response:", registerRes.data);

      // Extract customer_id from response
      const customerId = registerRes.data?.customerId;

      if (!customerId) {
        console.error("Customer ID missing in response:", registerRes.data);
        const serverMsg =
          registerRes.data?.message ||
          registerRes.data?.error ||
          "Unknown Error";
        toast.error(
          `Registration succeeded but Customer ID was not returned. Server says: ${serverMsg}`
        );
        setLoading(false);
        return;
      }

      toast.success(
        "User registered successfully. Proceeding to add details..."
      );

      // Step 2: Add User Details (With token as per requirement)
      // Updated payload to match the specific structure provided:
      // - Method changed to PUT
      // - Structure matches: customer_id, merchant_signup, last_level
      const detailsPayload = {
        customer_id: customerId,
        merchant_signup: {
          business_name: formData.business_name,
          entity_name: "",
          email_address: formData.email, // Passing the registered email
          number_of_fleets: "",
          country: "",
          state: "",
          business_type: ["1"],
        },
        last_level: 999,
      };

      await axiosInstance.put(API_URL.ADD_USER_DETAILS, detailsPayload);

      toast.success("User fully created and details added successfully!");
      // Reset form
      setFormData({
        email: "",
        password: "",
        user_type: "",
        business_name: "",
      });
    } catch (error: any) {
      console.error("Error in user creation flow:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "An error occurred during user creation";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-secondary text-white py-3">
              <h5 className="mb-0">Create New User</h5>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-12 mb-4">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="off"
                        placeholder="example@gmail.com"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Password</label>
                      <input
                        type="text"
                        name="password"
                        className="form-control"
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="new-password"
                        placeholder="Chris@289"
                      />
                      <small className="text-muted d-block mt-1">
                        Min 8 chars, 1 uppercase, 1 special char, 1 digit
                      </small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">User Type</label>
                      <select
                        name="user_type"
                        className="form-select"
                        value={formData.user_type}
                        onChange={handleChange}
                      >
                        <option value="">Select User Type</option>
                        <option value="AAA_USER">AAA_USER</option>
                        <option value="AFL_USER">AFL_USER</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        name="business_name"
                        className="form-control"
                        value={formData.business_name}
                        onChange={handleChange}
                        placeholder="Enter business name"
                      />
                    </div>
                  </div>
                </div>

                <div className="col-12 text-end mt-4">
                  <button
                    className="btn btn-primary px-5 py-2 fw-bold"
                    onClick={handleCreateUser}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Creating...
                      </>
                    ) : (
                      "Create User"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCreation;
